import React from 'react';
import TypeSource from '../../../../../sources/type';
import GenericFormField from '../../../../generic/GenericFormField';
import { ELEMENT_LIST_VIEW } from '../valuePickerUtils';
import { assocPath, pathOr, prepend } from 'ramda';
import { Alert, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import Table from '../../../../Table';
import '../../../../../../css/type-picker.less';
import { contentTypesList } from '../../../../../constants';
import Model from '../../../../../utils/model';
import Select from 'react-select';
import { selectStyles } from '../../../../../utils/core';

// Currently we don't support Object or List, so take that from the options for users
const primitiveContentTypesList = contentTypesList.filter(type => type.label !== 'Object' && type.label !== 'List');

const emptyTypeObject = {
    access: 'PRIVATE',
    elementType: 'TYPE',
    contentFormat: '',
    developerName: '',
    contentType: null,
    typeElementId: null,
    initializationOperations: null,
    isFixed: false,
    isVersionless: false,
};

const emptyTypePropertyObject = {
    contentFormat: '',
    contentType: null,
    developerName: '',
    id: Model.guid(),
    typeElementDeveloperName: null,
    typeElementId: null,
};

/**
 * @param {String} typeId ID for an existing type to modify
 * @param {Function} switchView Callback to enable switching back to type list view
 * @param {Function} select Callback for setting the type as selected once it has been created/modified
 * @description A component that allows for the creation and modification of types
 */
export class SingleType extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            type: null,
            errors: [],
        };
    }

    componentDidMount() {
        const { typeId, developerName, authenticationToken, tenantId } = this.props;
        if (typeId) {
            TypeSource.get(typeId, authenticationToken, tenantId)
                .then((response) => {
                    this.setState({ type: response });
                })
                .catch((error) => {
                    const errors = prepend({ message: error.message }, this.state.errors);
                    this.setState({ errors });
                });
        }
        const startingTypeObject = emptyTypeObject;
        startingTypeObject.developerName = developerName || '';
        this.setState({ type: emptyTypeObject });
    }

    onSubmit = (e) => {
        const { authenticationToken, tenantId } = this.props;

        e.preventDefault();
        const formElement = e.target;
        return TypeSource.save(this.state.type, tenantId, authenticationToken)
            .then(response => this.props.select(response))
            .catch((error) => {
                const errors = prepend({ message: error.message }, this.state.errors);
                this.setState({ errors });
                // Scroll to the top of the form to see the error
                formElement.scrollIntoView(true);
            });
    }

    updateType = (value, path) => {
        const updatedType = assocPath(path, value, this.state.type);
        this.setState({ type: updatedType });
    }

    addToType = () => {
        const { type } = this.state;
        if (Array.isArray(type.properties)) {
            type.properties.push(emptyTypePropertyObject);
        } else {
            type.properties = [emptyTypePropertyObject];
        }
        this.setState({ type });
    }

    removeFromType = (index) => {
        const { type } = this.state;
        type.properties.splice(index, 1);
        this.setState({ type });
    }

    clearError = (index) => {
        const { errors } = this.state;
        errors.splice(index, 1);
        this.setState({ errors });
    }

    render() {
        const typeColumns = [
            {
                renderHeader: () => 'Actions',
                renderCell: ({ item, rowIndex }) => (
                    <div className="button-row">
                        <button
                            key={item.id}
                            onClick={() => this.removeFromType(rowIndex)}
                            className="btn-icon danger"
                            title="Remove Property"
                            type="button"
                        >
                            <span className="glyphicon glyphicon-trash" />
                        </button>
                    </div>
                ),
            },
            {
                renderHeader: () => 'Property Name',
                renderCell: ({ item, rowIndex }) => (
                    <GenericFormField
                        key={item.id}
                        value={item.developerName}
                        onChange={e => this.updateType(e.target.value, ['properties', rowIndex, 'developerName'])}
                    />
                ),
            },
            {
                renderHeader: () => 'Primitive Type',
                renderCell: ({ item, rowIndex }) => {
                    const contentType = primitiveContentTypesList.find(type => type.key.toUpperCase() ===
                        pathOr('', ['contentType'], item).toUpperCase());
                    return (
                        <div className="mw-select">
                            <div>
                                <Select
                                    key={item.id}
                                    styles={selectStyles}
                                    onChange={type => this.updateType(
                                        type && type.value ? `Content${type.value}` : '',
                                        ['properties', rowIndex, 'contentType'],
                                    )}
                                    placeholder="Value Kind"
                                    value={{ label: contentType ? contentType.label : item.contentType }}
                                    options={
                                        primitiveContentTypesList.map(
                                            type => ({ label: type.label, value: type.label }),
                                        )
                                    }
                                />
                            </div>
                        </div>
                    );
                },
            },
            {
                renderHeader: () => 'Content Format',
                renderCell: ({ item, rowIndex }) => (
                    <GenericFormField
                        key={item.id}
                        value={item.contentFormat}
                        onChange={e => this.updateType(e.target.value, ['properties', rowIndex, 'contentFormat'])}
                    />
                ),
            },
        ];
        if (pathOr([], ['properties'], this.state.type).find(property => pathOr('', ['contentType'], property).toUpperCase() === 'CONTENTOBJECT' ||
            pathOr('', ['contentType'], property).toUpperCase() === 'CONTENTLIST')) {
            typeColumns.push({
                renderHeader: () => 'Type',
                renderCell: ({ item }) => pathOr('', ['contentType'], item).toUpperCase() === 'CONTENTOBJECT' ||
                    pathOr('', ['contentType'], item).toUpperCase() === 'CONTENTLIST' ?
                    item.typeElementId ?
                        item.typeElementId :
                        'Type element selection is not supported yet' :
                    null,
            });
        }
        return (
            <Modal.Body>
                <form onSubmit={this.onSubmit}>
                    {
                        this.state.errors.length > 0 ?
                            this.state.errors.map((error, index) => (
                                <Alert
                                    onDismiss={() => this.clearError(index)}
                                    bsStyle="danger"
                                    key={index}
                                >
                                    <p><strong>{error.message}</strong></p>
                                </Alert>
                            )) :
                            null
                    }
                    {this.state.type ?
                        <>
                            <h2>
                                {`Type: ${pathOr('', ['developerName'], this.state.type)}`}
                            </h2>
                            <GenericFormField
                                value={pathOr('', ['developerName'], this.state.type)}
                                onChange={e => this.updateType(e.target.value, ['developerName'])}
                                label="Name"
                                isRequired
                            />
                            <div className="flex margin-bottom">
                                <button
                                    onClick={() => this.addToType()}
                                    className="btn btn-icon btn-success flex-child-right"
                                    title="Add Property"
                                    type="button"
                                >
                                    <span className="glyphicon glyphicon-plus margin-right" />
                                    {'Add Property'}
                                </button>
                            </div>
                            <Table
                                className="table scroll-table-entry"
                                columns={typeColumns}
                                items={pathOr([], ['properties'], this.state.type)}
                            />
                            <button className="outcome btn btn-default btn-outline" onClick={() => this.props.switchView(ELEMENT_LIST_VIEW)}>
                                {'Cancel'}
                            </button>
                            <button className="outcome btn btn-primary" type="submit">Save</button>
                        </>
                        : null}
                </form>
            </Modal.Body>
        );
    }
}

const mapStateToProps = ({ currentUser: { authenticationToken, tenantId } }) => ({ authenticationToken, tenantId });

export default connect(mapStateToProps)(SingleType);
